// 月報 (2026-10-04) — GET /api/v1/reports/monthly。 形狀見 routes/monthly-report.ts。
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ApiError, type DevActorPreset, api } from './api.ts';
import type { BalanceSheet, IncomeStatement } from './use-accounting.ts';

export interface OpenItems {
  count: number;
  total: string;
  overdue: string;
  items: {
    name: string | null;
    ref: string | null;
    due_date: string | null;
    amount: string;
    overdue: boolean;
  }[];
}

export interface MonthlyReport {
  entity: { id: string; code: string | null; name: string; kind: 'gp' | 'fund' | string };
  period: { year: number; month: number; as_of: string };
  basis: 'management';
  generated_at: string;
  period_complete: boolean;
  close: {
    locked: boolean;
    draft_count: number;
    posted_count: number;
    bank_account_count: number;
    unreconciled_count: number;
    withholding_unremitted_count: number;
    withholding_unremitted_amount: string;
    gl_auto_post: boolean;
    self_approved: {
      id: string;
      request_no: string | null;
      title: string;
      amount: string | null;
      requester_name: string;
    }[];
  };
  pnl: { month: IncomeStatement; prev_month: IncomeStatement; ytd: IncomeStatement };
  balance_sheet: BalanceSheet;
  company: {
    receivables: OpenItems;
    payables: OpenItems;
    payroll: { headcount: number; gross_total: string; transferred_on: string | null } | null;
  } | null;
  fund: {
    id: string;
    name: string | null;
    code: string | null;
    investors: number;
    committed: string;
    paid_in: string;
    unfunded: string;
    invested: string;
    fees_paid: string;
    cash_available: string;
    calls_ytd: {
      call_number: number;
      call_type: string;
      call_date: string;
      due_date: string;
      total_amount: string;
      status: string;
      paid_from_fund_cash: boolean;
      purpose: string | null;
    }[];
    portfolio: {
      project_name: string;
      round_name: string | null;
      commitment_amount: string;
      investment_amount: string;
      paid_in: string;
      unfunded: string;
      money_in_date: string | null;
      ownership_pct: string | null;
      valuation_date: string | null;
      value: string;
      valued: boolean;
    }[];
  } | null;
}

export function useMonthlyReport(
  actor: DevActorPreset,
  entityId: string,
  year: number,
  month: number,
) {
  return useQuery<MonthlyReport, ApiError>({
    queryKey: ['monthly-report', entityId, year, month, actor],
    enabled: !!entityId,
    queryFn: () =>
      api<MonthlyReport>(
        `/api/v1/reports/monthly?ledger_entity_id=${entityId}&year=${year}&month=${month}`,
        { actor },
      ),
  });
}

// ── V484 凍結版 / 預覽 / 寄出 ────────────────────────────────────────────────

export interface SavedMonthlyReport {
  id: string;
  ledger_entity_id: string;
  period_year: number;
  period_month: number;
  content: MonthlyReport;
  commentary: string | null;
  generated_at: string;
  generated_by_name: string | null;
  sent_at: string | null;
  sent_by_name: string | null;
  sent_to: string[] | null;
}

export function useSavedMonthlyReport(
  actor: DevActorPreset,
  entityId: string,
  year: number,
  month: number,
) {
  return useQuery<SavedMonthlyReport | null, ApiError>({
    queryKey: ['monthly-report-saved', entityId, year, month, actor],
    enabled: !!entityId,
    queryFn: () =>
      api<SavedMonthlyReport | null>(
        `/api/v1/reports/monthly/saved?ledger_entity_id=${entityId}&year=${year}&month=${month}`,
        { actor },
      ),
  });
}

export interface PendingMonthlyReport {
  id: string;
  ledger_entity_id: string;
  entity_name: string;
  entity_code: string | null;
  period_year: number;
  period_month: number;
  generated_at: string;
  /** 台北日期 (後端算好, 不在前端切 UTC 字串)。 */
  generated_on: string;
}

/** 已凍結、還沒寄的 —— 管理員的待辦。 非管理員後端回 []，這裡也不必打。 */
export function usePendingMonthlyReports(actor: DevActorPreset, enabled: boolean) {
  return useQuery<PendingMonthlyReport[], ApiError>({
    queryKey: ['monthly-report-pending', actor],
    enabled,
    queryFn: () => api<PendingMonthlyReport[]>('/api/v1/reports/monthly/pending', { actor }),
    staleTime: 60_000,
  });
}

export interface MonthlyReportPreview {
  recipients: { name: string; email: string }[];
  subject: string;
  html: string;
  /** 2026-10-05: 信裡附的 PDF (伺服器產生, 跟 /saved/:id/pdf 同一份)。 */
  attachment: { name: string; size: number };
  sent_at: string | null;
}

export function monthlyReportPdfPath(id: string): string {
  return `/api/v1/reports/monthly/saved/${id}/pdf`;
}

export function useMonthlyReportMutations(actor: DevActorPreset) {
  const qc = useQueryClient();
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['monthly-report-saved'] }),
      qc.invalidateQueries({ queryKey: ['monthly-report-pending'] }),
    ]);
  const generate = useMutation<
    { id: string },
    ApiError,
    { entityId: string; year: number; month: number }
  >({
    mutationFn: ({ entityId, year, month }) =>
      api<{ id: string }>('/api/v1/reports/monthly/saved', {
        actor,
        method: 'POST',
        json: { ledger_entity_id: Number(entityId), year, month },
      }),
    onSuccess: refresh,
  });
  const saveCommentary = useMutation<unknown, ApiError, { id: string; commentary: string }>({
    mutationFn: ({ id, commentary }) =>
      api(`/api/v1/reports/monthly/saved/${id}`, {
        actor,
        method: 'PATCH',
        json: { commentary: commentary.trim() ? commentary : null },
      }),
    onSuccess: refresh,
  });
  const send = useMutation<{ sent: string[]; failed: string[] }, ApiError, { id: string }>({
    mutationFn: ({ id }) =>
      api<{ sent: string[]; failed: string[] }>(`/api/v1/reports/monthly/saved/${id}/send`, {
        actor,
        method: 'POST',
        json: {},
      }),
    onSuccess: refresh,
  });
  return { generate, saveCommentary, send };
}

export function fetchMonthlyReportPreview(actor: DevActorPreset, id: string) {
  return api<MonthlyReportPreview>(`/api/v1/reports/monthly/saved/${id}/preview`, { actor });
}
